import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const COOKIE_CONSENT_KEY = "paddock69_cookie_consent";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash on load
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (analytics: boolean) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ 
      accepted: true, 
      analytics, 
      date: new Date().toISOString() 
    }));
    setVisible(false);
  };

  const acceptAll = () => savePreferences(true);
  const rejectAll = () => savePreferences(false);
  const saveCustom = () => savePreferences(analyticsEnabled);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4"
        >
          <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto bg-card border border-border rounded-2xl p-5 shadow-2xl shadow-black/50">
            
            {showSettings ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <h3 className="font-mono font-semibold text-foreground flex items-center gap-2">
                    <Settings2 className="w-5 h-5 text-primary" />
                    Preferencias de Privacidad
                  </h3>
                  <button onClick={() => setShowSettings(false)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4 py-2">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Cookies Esenciales</Label>
                      <p className="text-xs text-muted-foreground w-[80%]">Requeridas para sesiones, seguridad (PitStop Chat) y funcionamiento básico. No pueden desactivarse.</p>
                    </div>
                    <Switch checked={true} disabled />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Cookies Analíticas</Label>
                      <p className="text-xs text-muted-foreground w-[80%]">Nos ayudan a entender cómo mejoras tus tiempos y cómo usas la app de forma anónima.</p>
                    </div>
                    <Switch checked={analyticsEnabled} onCheckedChange={setAnalyticsEnabled} />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <Button onClick={saveCustom} className="btn-racing h-9 px-6">
                    Guardar Preferencias
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <Shield className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-mono text-base font-semibold text-foreground mb-1">
                    Tu Privacidad en el Paddock
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Usamos cookies propias para que la plataforma fluya como un reloj suizo. 
                    Puedes configurarlas a tu gusto y controlar tus datos. {" "}
                    <button
                      onClick={() => navigate("/privacy")}
                      className="text-primary hover:underline font-mono"
                    >
                      Política completa
                    </button>
                  </p>

                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <Button onClick={acceptAll} size="default" className="btn-racing font-semibold rounded-none">
                      Gas a Todas
                    </Button>
                    <Button onClick={() => setShowSettings(true)} variant="outline" size="default" className="border-border">
                      <Settings2 className="w-4 h-4 mr-2" />
                      Ajustes
                    </Button>
                    <Button onClick={rejectAll} variant="ghost" size="default" className="text-muted-foreground text-xs hover:text-foreground">
                      Solo esenciales
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
